// External Imports
import { useContext, useEffect, useState } from "react";
import Heading from "../UI/Heading";
import { MdAdd } from "react-icons/md";
import { requestAdminAPI } from "../../api/posh-api";
import LoadingCard from "../UI/LoadingCard";
import NoRecordsFound from "../UI/NoRecordsFound";
import { isoDateToString } from "../../util/format-date";
import { UtilityContext } from "../../context/util-context";
import { useLocation } from "react-router-dom";
import Radio from "../UI/Radio";
import AddService from "./AddService";
import { differenceInMonths } from "date-fns";
import EditService from "./EditService";
import ExportCsvButton from "../UI/ExportCsvButton";

// Internal Imports

/** ========================================================================
 * * Service Component
 * This component renders a list of scheduled services.
 * ========================================================================== */

export default function Service() {
  const { navigate } = useContext(UtilityContext);
  const { search } = useLocation();
  const params = new URLSearchParams(search);

  // Default Filter
  const defaultFilters = {
    status: "active",
  };

  // Filters in URL params
  const paramFilters = {};
  Object.keys(defaultFilters).forEach(
    (key) => (paramFilters[key] = params.get(key) ?? defaultFilters[key])
  );

  const [loading, setLoading] = useState(true);
  const [services, setServices] = useState([]);
  const [filter, setFilter] = useState(paramFilters);
  const [add, setAdd] = useState(false);
  const [selected, setSelected] = useState();

  /* ====================== FETCH SERVICES ====================== */
  const fetchRecords = async () => {
    try {
      setLoading(true);
      let filters = [];
      if (filter.status) filters.push(`status: {eq: "${filter.status}"}`);

      // Send Request
      const res = await requestAdminAPI("filter", {
        table: "service",
        filters: filters,
        columns: ["vehicle { make { name } vin year model }"],
      });

      // Replace URL
      let url = "/service?";
      Object.keys(filter).forEach((key) => {
        url += `${key}=${filter[key] ?? defaultFilters[key]}&`;
      });
      navigate(url, {}, { replace: true });

      setServices(res);
      setLoading(false);
    } catch (err) {
      setLoading(false);
      console.error(err);
    }
  };

  useEffect(() => {
    fetchRecords();
  }, [filter]);

  return (
    <div className="container">
      <AddService open={add} setOpen={setAdd} onComplete={fetchRecords} />
      <EditService
        open={selected ? true : false}
        setOpen={setSelected}
        service={selected}
        onComplete={fetchRecords}
      />
      <Heading
        title="Service"
        text="Services are used to auto-generate maintenance requests. For example, a vehicle with a service interval of 5000 miles will trigger a maintenance request every 5000 miles."
      >
        <MdAdd className="text-3xl icon" onClick={() => setAdd(true)} />
        <ExportCsvButton array={services} filename="service" />
      </Heading>
      <div className="grid grid-cols-2 gap-4">
        <Radio
          options={["Active", "Paused"]}
          selected={filter.status}
          onClick={(s) => setFilter({ ...filter, status: s })}
        />
      </div>
      {loading ? (
        <LoadingCard columns={5} max={4} />
      ) : services.length > 0 ? (
        <div className="list">
          {services.map((service, idx) => (
            <ServiceCard
              key={idx}
              service={service}
              setSelected={setSelected}
            />
          ))}
        </div>
      ) : (
        <NoRecordsFound />
      )}
    </div>
  );
}

const ServiceCard = ({ service, setSelected }) => {
  const calculateNextDueDate = () => {
    const currentDate = new Date();
    if (new Date(service.dueDate) > currentDate) {
      const months = differenceInMonths(
        new Date(service.dueDate),
        new Date(service.dateCreated)
      );
      currentDate.setMonth(currentDate.getMonth() + months);
      return currentDate.toISOString();
    } else return service.dueDate;
  };
  if (!service) return null;

  return (
    <div
      onClick={() => setSelected(service)}
      className="grid gap-4 lg:grid-cols-5 pill"
    >
      <p className="text-stone-400">{isoDateToString(service.dateCreated)}</p>
      <p className="font-semibold">
        {service.vehicle.year} {service.vehicle.make.name}{" "}
        {service.vehicle.modal} {service.vehicle.vin.substring(13, 17)}
      </p>
      <p className="">{service.description}</p>
      <p className="text-stone-400">{service.intervalMiles}mi</p>
      <p className="font-semibold">{isoDateToString(calculateNextDueDate())}</p>
    </div>
  );
};
